import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Divider,
    Col,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography
} from 'antd';
import {
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { AppState } from '../store';
import { getPlans, getRolSystems, getServices, postPlan, putPlan } from '../store/user/thunks';
import { Plan, PlanFeatureFlags, PlanRequest, PlanRolSystem, PlanService } from '../store/user/types';

const { Title, Text } = Typography;
const BYTES_PER_GB = 1024 * 1024 * 1024;
const SERVICE_RESOURCE_TYPES = {
    ESTABLECIMENTO: 'ESTABLECIMENTO',
    CAPACIDADES_CLINICAS: 'CAPACIDADES_CLINICAS',
    SERVICIOS_CLINICOS: 'SERVICIOS_CLINICOS'
} as const;

const getServiceResourceType = (service?: PlanService) => {
    const resourceType = String(service?.resourceType || '').trim().toUpperCase();
    if (resourceType) {
        return resourceType;
    }

    // Fallback while older gateway instances still return only category.
    const category = String(service?.category || '').trim().toUpperCase();
    if (category === 'ESTABLECIMENTO') {
        return SERVICE_RESOURCE_TYPES.ESTABLECIMENTO;
    }
    if (category === 'FICHAS MEDICAS') {
        return SERVICE_RESOURCE_TYPES.CAPACIDADES_CLINICAS;
    }
    return category ? SERVICE_RESOURCE_TYPES.SERVICIOS_CLINICOS : 'OTROS';
};

const getServiceResourceLabel = (resourceType: string) => {
    switch (resourceType) {
        case SERVICE_RESOURCE_TYPES.ESTABLECIMENTO:
            return 'Establecimiento (seleccione uno)';
        case SERVICE_RESOURCE_TYPES.CAPACIDADES_CLINICAS:
            return 'Capacidades clínicas';
        case SERVICE_RESOURCE_TYPES.SERVICIOS_CLINICOS:
            return 'Servicios clínicos';
        default:
            return resourceType.replace(/_/g, ' ');
    }
};

const money = (value: number) =>
    new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(value || 0);

type RoleCapacityForm = Record<string, number | null>;
type RoleNumberForm = Record<string, number>;

const initialPlanValues: PlanRequest & {
    roleCapacities: RoleCapacityForm;
    roleIncludedUsers: RoleNumberForm;
    roleAdditionalLicensePrices: RoleNumberForm;
} = {
    name: '',
    description: '',
    type: 'PAID',
    price: 0,
    billingCycle: 'MENSUAL',
    billingPeriodDays: null,
    trialDays: null,
    trialValidationAmount: null,
    visible: true,
    status: 'ACTIVO',
    rolSystemIds: [],
    serviceIds: [],
    roleCapacities: {},
    roleIncludedUsers: {},
    roleAdditionalLicensePrices: {},
    featureFlags: {
        maxSpecialities: null,
        maxPatients: null,
        maxCenters: null,
        maxAppointments: null,
        maxEmails: null,
        maxAttentions: null,
        maxStorageBytes: null,
        maxStorageGb: null
    } as any
};

const PlanManagement: React.FC<any> = (props: any) => {
    const [form] = Form.useForm();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [rolSystems, setRolSystems] = useState<PlanRolSystem[]>([]);
    const [services, setServices] = useState<PlanService[]>([]);
    const [search, setSearch] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

    const filteredPlans = useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) {
            return plans;
        }

        return plans.filter(plan =>
            String(plan.code || '').toLowerCase().includes(value)
            || plan.name.toLowerCase().includes(value)
            || String(plan.description || '').toLowerCase().includes(value)
            || plan.type.toLowerCase().includes(value)
            || plan.billingCycle.toLowerCase().includes(value)
            || plan.status.toLowerCase().includes(value));
    }, [plans, search]);

    const serviceById = useMemo(() => (
        new Map(services.map(service => [service.id, service]))
    ), [services]);

    const serviceOptions = useMemo(() => {
        const groupedServices = services.reduce((accumulator, service) => {
            const resourceType = getServiceResourceType(service);
            const resourceServices = accumulator.get(resourceType) || [];
            resourceServices.push(service);
            accumulator.set(resourceType, resourceServices);
            return accumulator;
        }, new Map<string, PlanService[]>());

        const resourceTypeOrder: string[] = [
            SERVICE_RESOURCE_TYPES.ESTABLECIMENTO,
            SERVICE_RESOURCE_TYPES.CAPACIDADES_CLINICAS,
            SERVICE_RESOURCE_TYPES.SERVICIOS_CLINICOS
        ];

        return Array.from(groupedServices.entries())
            .sort(([resourceTypeA], [resourceTypeB]) => {
                const orderA = resourceTypeOrder.indexOf(resourceTypeA);
                const orderB = resourceTypeOrder.indexOf(resourceTypeB);
                if (orderA === -1 && orderB === -1) {
                    return resourceTypeA.localeCompare(resourceTypeB);
                }
                if (orderA === -1) {
                    return 1;
                }
                if (orderB === -1) {
                    return -1;
                }
                return orderA - orderB;
            })
            .map(([resourceType, resourceServices]) => ({
                label: getServiceResourceLabel(resourceType),
                options: resourceServices
                    .sort((serviceA, serviceB) => serviceA.name.localeCompare(serviceB.name))
                    .map(service => ({
                        value: service.id,
                        label: service.name
                    }))
            }));
    }, [services]);

    const normalizeServiceSelection = (values: string[], warn = true) => {
        const selectedServiceIds = Array.from(new Set((values || []).filter(Boolean)));
        const establishmentIds = selectedServiceIds.filter(serviceId => (
            getServiceResourceType(serviceById.get(serviceId)) === SERVICE_RESOURCE_TYPES.ESTABLECIMENTO
        ));

        if (establishmentIds.length <= 1) {
            return selectedServiceIds;
        }

        if (warn) {
            message.warning('Solo puede seleccionar un servicio de tipo establecimiento.');
        }

        const lastSelectedEstablishment = establishmentIds[establishmentIds.length - 1];
        return selectedServiceIds.filter(serviceId => (
            getServiceResourceType(serviceById.get(serviceId)) !== SERVICE_RESOURCE_TYPES.ESTABLECIMENTO
            || serviceId === lastSelectedEstablishment
        ));
    };

    const loadPlans = async () => {
        const response = await props.getPlans();
        setPlans(response || []);
    };

    const loadCatalogs = async () => {
        const [rolSystemResponse, serviceResponse] = await Promise.all([
            props.getRolSystems(),
            props.getServices()
        ]);
        setRolSystems(rolSystemResponse || []);
        setServices(serviceResponse || []);
    };

    useEffect(() => {
        loadPlans();
        loadCatalogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateModal = () => {
        setSelectedPlan(null);
        form.resetFields();
        form.setFieldsValue(initialPlanValues);
        setIsModalOpen(true);
    };

    const openEditModal = (plan: Plan) => {
        setSelectedPlan(plan);
        form.resetFields();
        form.setFieldsValue({
            ...plan,
            price: Number(plan.price || 0),
            visible: plan.visible !== false,
            rolSystemIds: (plan.rolSystems || []).map(item => item.rolSystemId),
            roleCapacities: (plan.rolSystems || []).reduce((result: RoleCapacityForm, item: any) => {
                result[item.rolSystemId] = item.maxUsers ?? 0;
                return result;
            }, {}),
            roleIncludedUsers: (plan.rolSystems || []).reduce((result: RoleNumberForm, item: any) => {
                result[item.rolSystemId] = Number(item.includedUsers ?? 0);
                return result;
            }, {}),
            roleAdditionalLicensePrices: (plan.rolSystems || []).reduce((result: RoleNumberForm, item: any) => {
                result[item.rolSystemId] = Number(item.additionalLicensePrice ?? 0);
                return result;
            }, {}),
            serviceIds: normalizeServiceSelection((plan.services || []).map(item => item.id), false),
            featureFlags: normalizeFeatureFlagsForForm(plan.featureFlagsResponse)
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedPlan(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const trialDays = values.type === 'FREE' ? null : (values.trialDays ? Number(values.trialDays) : null);
            const roleCapacities: RoleCapacityForm = values.roleCapacities || {};
            const roleIncludedUsers: RoleNumberForm = values.roleIncludedUsers || {};
            const roleAdditionalLicensePrices: RoleNumberForm = values.roleAdditionalLicensePrices || {};
            const request: PlanRequest & { rolSystemCapacities: Array<{ rolSystemId: string; maxUsers: number | null; includedUsers: number; additionalLicensePrice: number }> } = {
                id: selectedPlan?.id || null,
                code: selectedPlan?.code || null,
                name: values.name,
                description: values.description || null,
                type: values.type,
                price: values.type === 'FREE' ? 0 : Number(values.price || 0),
                billingCycle: values.billingCycle,
                billingPeriodDays: values.billingCycle === 'DIAS' ? Number(values.billingPeriodDays || 0) : null,
                trialDays,
                trialValidationAmount: values.type === 'FREE' || !trialDays ? null : Number(values.trialValidationAmount || 0),
                visible: values.visible !== false,
                status: values.status,
                rolSystemIds: values.rolSystemIds || [],
                rolSystemCapacities: (values.rolSystemIds || []).map((rolSystemId: string) => ({
                    rolSystemId,
                    maxUsers: roleCapacities[rolSystemId] === null || roleCapacities[rolSystemId] === undefined
                        ? 0
                        : Number(roleCapacities[rolSystemId]),
                    includedUsers: Number(roleIncludedUsers[rolSystemId] || 0),
                    additionalLicensePrice: Number(roleAdditionalLicensePrices[rolSystemId] || 0)
                })),
                serviceIds: normalizeServiceSelection(values.serviceIds || []),
                featureFlags: normalizeFeatureFlagsForRequest(values.featureFlags)
            };

            const response = selectedPlan
                ? await props.putPlan(request)
                : await props.postPlan(request);

            if (response) {
                await loadPlans();
                closeModal();
            }
        } catch (error: any) {
            const firstError = error?.errorFields?.[0];
            if (firstError) {
                form.scrollToField(firstError.name);
                message.warning(firstError.errors?.[0] || 'Revise los campos requeridos del plan');
                return;
            }

            throw error;
        }
    };

    const renderLimitField = (label: string, fieldName: keyof PlanFeatureFlags) => (
        <Form.Item shouldUpdate noStyle>
            {() => {
                const currentValue = form.getFieldValue(['featureFlags', fieldName]);
                const isUnlimited = currentValue === null || currentValue === undefined;

                return (
                    <Form.Item label={label} style={{ marginBottom: 16 }}>
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Switch
                                checked={isUnlimited}
                                checkedChildren="Ilimitado"
                                unCheckedChildren="Con limite"
                                onChange={(checked) => {
                                    form.setFieldValue(['featureFlags', fieldName], checked ? null : 0);
                                }}
                            />
                            <Form.Item name={['featureFlags', fieldName]} noStyle>
                                <InputNumber
                                    min={0}
                                    disabled={isUnlimited}
                                    placeholder={isUnlimited ? 'Ilimitado' : 'Ingrese limite'}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Space>
                    </Form.Item>
                );
            }}
        </Form.Item>
    );

    const renderStorageLimitField = () => (
        <Form.Item shouldUpdate noStyle>
            {() => {
                const currentValue = form.getFieldValue(['featureFlags', 'maxStorageGb']);
                const isUnlimited = currentValue === null || currentValue === undefined;

                return (
                    <Form.Item label="Max. almacenamiento (GB)" style={{ marginBottom: 16 }}>
                        <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Switch
                                checked={isUnlimited}
                                checkedChildren="Ilimitado"
                                unCheckedChildren="Con limite"
                                onChange={(checked) => {
                                    form.setFieldValue(['featureFlags', 'maxStorageGb'], checked ? null : 0);
                                }}
                            />
                            <Form.Item name={['featureFlags', 'maxStorageGb']} noStyle>
                                <InputNumber
                                    min={0}
                                    precision={2}
                                    step={1}
                                    disabled={isUnlimited}
                                    placeholder={isUnlimited ? 'Ilimitado' : 'Ej. 10'}
                                    addonAfter="GB"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Space>
                    </Form.Item>
                );
            }}
        </Form.Item>
    );

    const normalizeFeatureFlags = (value?: Partial<PlanFeatureFlags> | null): PlanFeatureFlags => ({
        maxSpecialities: value?.maxSpecialities ?? null,
        maxPatients: value?.maxPatients ?? null,
        maxCenters: value?.maxCenters ?? null,
        maxAppointments: value?.maxAppointments ?? null,
        maxEmails: value?.maxEmails ?? null,
        maxAttentions: value?.maxAttentions ?? null,
        maxStorageBytes: value?.maxStorageBytes ?? null
    } as PlanFeatureFlags);

    const normalizeFeatureFlagsForForm = (value?: Partial<PlanFeatureFlags> | null) => ({
        ...normalizeFeatureFlags(value),
        maxStorageGb: bytesToGigabytes(value?.maxStorageBytes)
    });

    const normalizeFeatureFlagsForRequest = (value?: (Partial<PlanFeatureFlags> & { maxStorageGb?: number | null }) | null): PlanFeatureFlags => ({
        ...normalizeFeatureFlags(value),
        maxStorageBytes: gigabytesToBytes(value?.maxStorageGb)
    });

    const bytesToGigabytes = (value?: number | null) => (
        value === null || value === undefined ? null : Number((value / BYTES_PER_GB).toFixed(2))
    );

    const gigabytesToBytes = (value?: number | null) => (
        value === null || value === undefined ? null : Math.round(Number(value) * BYTES_PER_GB)
    );

    const columns = [
        {
            title: 'Codigo',
            dataIndex: 'code',
            key: 'code',
            width: 110,
            render: (value: string) => <Text strong>{value}</Text>
        },
        {
            title: 'Plan',
            dataIndex: 'name',
            key: 'name',
            render: (value: string, row: Plan) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{value}</Text>
                    <Text type="secondary">{row.description}</Text>
                </Space>
            )
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            width: 130,
            render: (value: string) => <Tag color="blue">{value}</Tag>
        },
        {
            title: 'Ciclo',
            dataIndex: 'billingCycle',
            key: 'billingCycle',
            width: 130,
            render: (value: string, row: Plan) => (
                <Tag>{value === 'DIAS' && row.billingPeriodDays ? `${value} (${row.billingPeriodDays})` : value}</Tag>
            )
        },
        {
            title: 'Prueba',
            dataIndex: 'trialDays',
            key: 'trialDays',
            width: 130,
            render: (value: number | null, row: Plan) => (
                row.type === 'FREE' || !value
                    ? <Text type="secondary">Sin prueba</Text>
                    : <Tag color="purple">{value} dias</Tag>
            )
        },
        {
            title: 'Precio',
            dataIndex: 'price',
            key: 'price',
            width: 130,
            align: 'right' as const,
            render: (value: number) => money(value)
        },
        {
            title: 'Visible',
            dataIndex: 'visible',
            key: 'visible',
            width: 120,
            render: (value: boolean | null | undefined) => (
                value === false
                    ? <Tag color="default">OCULTO</Tag>
                    : <Tag color="green">VISIBLE</Tag>
            )
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'red'}>{value}</Tag>
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            align: 'right' as const,
            render: (_: unknown, row: Plan) => (
                <Button icon={<EditOutlined />} onClick={() => openEditModal(row)}>
                    Editar
                </Button>
            )
        }
    ];

    return (
        <div className="tbc-admin-page">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4 }}>Gestion de planes</Title>
                    <Text type="secondary">Creacion y administracion de planes mensuales, anuales y por dias.</Text>
                </div>

                <Card>
                    <Row gutter={[12, 12]} align="bottom">
                        <Col xs={24} md={12}>
                            <Text strong>Buscar plan</Text>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Codigo, nombre, tipo, ciclo o estado"
                                style={{ marginTop: 6 }}
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<ReloadOutlined />} block onClick={loadPlans}>
                                Actualizar
                            </Button>
                        </Col>
                        <Col xs={24} md={4}>
                            <Button icon={<PlusOutlined />} type="primary" block onClick={openCreateModal}>
                                Nuevo plan
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredPlans}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 900 }}
                />

                <Modal
                    open={isModalOpen}
                    title={selectedPlan ? 'Editar plan' : 'Nuevo plan'}
                    width={820}
                    className="tbc-admin-modal"
                    okText={selectedPlan ? 'Actualizar' : 'Crear'}
                    cancelText="Cancelar"
                    onOk={handleSubmit}
                    onCancel={closeModal}
                    destroyOnHidden
                    forceRender
                >
                    <Form form={form} layout="vertical" initialValues={initialPlanValues}>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Nombre"
                                    name="name"
                                    rules={[{ required: true, message: 'Ingrese el nombre del plan' }]}
                                >
                                    <Input placeholder="Standard" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Tipo"
                                    name="type"
                                    rules={[{ required: true, message: 'Seleccione el tipo' }]}
                                >
                                    <Select
                                        options={[
                                            { value: 'PAID', label: 'PAGADO' },
                                            { value: 'FREE', label: 'GRATIS' }
                                        ]}
                                        onChange={(value) => {
                                            if (value === 'FREE') {
                                                form.setFieldValue('price', 0);
                                                form.setFieldValue('trialDays', null);
                                                form.setFieldValue('trialValidationAmount', null);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item label="Descripcion" name="description">
                                    <Input.TextArea rows={3} placeholder="Describe el alcance comercial del plan" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item shouldUpdate noStyle>
                                    {() => {
                                        const isFreePlan = form.getFieldValue('type') === 'FREE';

                                        return (
                                            <Form.Item
                                                label="Precio"
                                                name="price"
                                                rules={[
                                                    { required: true, message: 'Ingrese el precio' },
                                                    {
                                                        validator: (_, value) => {
                                                            if (isFreePlan) {
                                                                return Number(value || 0) === 0
                                                                    ? Promise.resolve()
                                                                    : Promise.reject(new Error('El plan gratis debe tener precio 0'));
                                                            }

                                                            return Number(value || 0) >= 1
                                                                ? Promise.resolve()
                                                                : Promise.reject(new Error('Ingrese un precio minimo de $1.00 para planes pagados'));
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    precision={2}
                                                    disabled={isFreePlan}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Ciclo"
                                    name="billingCycle"
                                    rules={[{ required: true, message: 'Seleccione el ciclo' }]}
                                >
                                    <Select
                                        options={[
                                            { value: 'MENSUAL', label: 'MENSUAL' },
                                            { value: 'ANUAL', label: 'ANUAL' },
                                            { value: 'DIAS', label: 'DIAS' }
                                        ]}
                                        onChange={(value) => {
                                            if (value !== 'DIAS') {
                                                form.setFieldValue('billingPeriodDays', null);
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Form.Item shouldUpdate noStyle>
                                {() => form.getFieldValue('billingCycle') === 'DIAS' && (
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            label="Dias del plan"
                                            name="billingPeriodDays"
                                            rules={[{ required: true, message: 'Ingrese los dias del plan' }]}
                                        >
                                            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Form.Item>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Estado"
                                    name="status"
                                    rules={[{ required: true, message: 'Seleccione el estado' }]}
                                >
                                    <Select
                                        options={[
                                            { value: 'ACTIVO', label: 'ACTIVO' },
                                            { value: 'INACTIVO', label: 'INACTIVO' }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Visible para clientes"
                                    name="visible"
                                    valuePropName="checked"
                                    extra="Si esta apagado, no aparece en registros ni pantallas publicas."
                                >
                                    <Switch checkedChildren="Visible" unCheckedChildren="Oculto" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left">Prueba y tokenizacion</Divider>
                        <Form.Item shouldUpdate noStyle>
                            {() => {
                                const isFreePlan = form.getFieldValue('type') === 'FREE';
                                const hasTrialDays = !isFreePlan && Number(form.getFieldValue('trialDays') || 0) > 0;

                                return (
                                    <Row gutter={[16, 0]}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label="Dias gratis de prueba"
                                                name="trialDays"
                                                extra={isFreePlan ? 'No aplica para planes gratis.' : 'Opcional. Vacio significa que no hay dias de prueba.'}
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (isFreePlan || value === null || value === undefined || value === '') {
                                                                return Promise.resolve();
                                                            }

                                                            return Number(value) > 0
                                                                ? Promise.resolve()
                                                                : Promise.reject(new Error('Los dias de prueba deben ser mayores a 0'));
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber
                                                    min={1}
                                                    precision={0}
                                                    disabled={isFreePlan}
                                                    placeholder={isFreePlan ? 'No aplica' : 'Ej. 7'}
                                                    onChange={(value) => {
                                                        if (!value || Number(value) <= 0) {
                                                            form.setFieldValue('trialValidationAmount', null);
                                                        }
                                                    }}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                label="Monto de validacion de tarjeta"
                                                name="trialValidationAmount"
                                                extra={
                                                    isFreePlan
                                                        ? 'No aplica para planes gratis.'
                                                        : hasTrialDays
                                                            ? 'Se usa solo durante la prueba gratis. Minimo $1.00.'
                                                            : 'Sin dias de prueba, PayPhone cobra el precio del plan.'
                                                }
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (isFreePlan || !hasTrialDays) {
                                                                return Promise.resolve();
                                                            }

                                                            return Number(value || 0) >= 1
                                                                ? Promise.resolve()
                                                                : Promise.reject(new Error('Ingrese un monto minimo de $1.00'));
                                                        }
                                                    }
                                                ]}
                                            >
                                                <InputNumber
                                                    min={1}
                                                    precision={2}
                                                    step={1}
                                                    disabled={isFreePlan || !hasTrialDays}
                                                    placeholder={isFreePlan || !hasTrialDays ? 'No aplica' : '1.00'}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                            }}
                        </Form.Item>

                        <Divider orientation="left">Servicios incluidos</Divider>
                        <Form.Item label="Servicios" name="serviceIds">
                            <Select
                                mode="multiple"
                                allowClear
                                optionFilterProp="label"
                                placeholder="Seleccione los servicios del plan"
                                options={serviceOptions}
                                onChange={(values) => {
                                    form.setFieldsValue({ serviceIds: normalizeServiceSelection(values) });
                                }}
                            />
                        </Form.Item>

                        <Divider orientation="left">Roles y sistemas habilitados</Divider>
                        <Form.Item label="Accesos" name="rolSystemIds">
                            <Select
                                mode="multiple"
                                allowClear
                                optionFilterProp="label"
                                placeholder="Seleccione roles/sistemas disponibles para el plan"
                                onChange={(values) => {
                                    const currentCapacities: RoleCapacityForm = form.getFieldValue('roleCapacities') || {};
                                    const currentIncludedUsers: RoleNumberForm = form.getFieldValue('roleIncludedUsers') || {};
                                    const currentAdditionalLicensePrices: RoleNumberForm = form.getFieldValue('roleAdditionalLicensePrices') || {};
                                    const nextCapacities: RoleCapacityForm = {};
                                    const nextIncludedUsers: RoleNumberForm = {};
                                    const nextAdditionalLicensePrices: RoleNumberForm = {};
                                    values.forEach((rolSystemId: string) => {
                                        nextCapacities[rolSystemId] = currentCapacities[rolSystemId] ?? 0;
                                        nextIncludedUsers[rolSystemId] = currentIncludedUsers[rolSystemId] ?? 0;
                                        nextAdditionalLicensePrices[rolSystemId] = currentAdditionalLicensePrices[rolSystemId] ?? 0;
                                    });
                                    form.setFieldsValue({ roleCapacities: nextCapacities });
                                    form.setFieldsValue({
                                        roleIncludedUsers: nextIncludedUsers,
                                        roleAdditionalLicensePrices: nextAdditionalLicensePrices
                                    });
                                }}
                                options={rolSystems.map(rolSystem => ({
                                    value: rolSystem.rolSystemId,
                                    label: `${rolSystem.systemLabel || rolSystem.systemName || 'Sistema'} - ${rolSystem.rolName}`
                                }))}
                            />
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                            {() => {
                                const selectedRoleSystemIds: string[] = form.getFieldValue('rolSystemIds') || [];
                                if (selectedRoleSystemIds.length === 0) {
                                    return null;
                                }

                                return (
                                    <Card size="small" title="Capacidad por rol" style={{ marginBottom: 16 }}>
                                        <Text type="secondary">
                                            Configure el máximo, las licencias incluidas en el plan y el precio de cada licencia adicional. Use 0 en el máximo para indicar ilimitado.
                                        </Text>
                                        <Row gutter={[16, 0]} style={{ marginTop: 12 }}>
                                            {selectedRoleSystemIds.map((rolSystemId) => {
                                                const role = rolSystems.find(item => item.rolSystemId === rolSystemId);
                                                return (
                                                    <Col xs={24} key={rolSystemId}>
                                                        <Card size="small" type="inner" title={`${role?.systemLabel || role?.systemName || 'Sistema'} - ${role?.rolName || 'Rol'}`}>
                                                            <Row gutter={[12, 0]}>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Máximo de usuarios"
                                                                        name={['roleCapacities', rolSystemId]}
                                                                        rules={[{ type: 'number', min: 0, message: 'Use 0 o un número positivo' }]}
                                                                    >
                                                                        <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="0 = ilimitado" />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Licencias incluidas"
                                                                        name={['roleIncludedUsers', rolSystemId]}
                                                                        rules={[{ type: 'number', min: 0, message: 'Use 0 o un número positivo' }]}
                                                                    >
                                                                        <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="Ej. 1" />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col xs={24} md={8}>
                                                                    <Form.Item
                                                                        label="Precio licencia adicional"
                                                                        name={['roleAdditionalLicensePrices', rolSystemId]}
                                                                        rules={[{ type: 'number', min: 0, message: 'Ingrese un valor válido' }]}
                                                                    >
                                                                        <InputNumber min={0} precision={2} step={1} addonBefore="$" style={{ width: '100%' }} placeholder="Ej. 15" />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </Card>
                                );
                            }}
                        </Form.Item>

                        <Divider orientation="left">Limites y beneficios</Divider>
                        <Row gutter={[16, 0]}>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. especialidades', 'maxSpecialities')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. pacientes', 'maxPatients')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. centros', 'maxCenters')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. citas', 'maxAppointments')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. emails', 'maxEmails')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderLimitField('Max. atenciones', 'maxAttentions')}
                            </Col>
                            <Col xs={24} md={8}>
                                {renderStorageLimitField()}
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </Space>
        </div>
    );
};

const mapStateToProps = (_state: AppState) => ({
});

export default connect(mapStateToProps, {
    getPlans,
    getRolSystems,
    getServices,
    postPlan,
    putPlan
})(PlanManagement);
